import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { styled } from "@mui/material/styles";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import SkillsInput from "../common/SkillsInput";

import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
  Tab,
  Tabs,
  FormControl 
} from "@mui/material";
import {
  Upload,
  Close,
  Link as LinkIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Slideshow as SlideshowIcon,
  Videocam as VideocamIcon,
  Audiotrack as AudiotrackIcon,
  InsertDriveFile as InsertDriveFileIcon,
  TableChart as TableChartIcon,
  School as SchoolIcon,
  FolderZip as FolderZipIcon,
} from "@mui/icons-material";
import { jobAidService } from "../../services/api";

const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: "12px",
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
  },
}));

const PreviewImage = styled("img")({
  maxWidth: "100%",
  maxHeight: "200px",
  objectFit: "cover",
  borderRadius: "8px",
});

const modules = {
  toolbar: {
    container: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["clean"],
    ],
  },
  clipboard: { matchVisual: false },
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

/**
 * ContentForm is a React component for creating and editing content.
 * It displays a form with fields for entering content name, description, duration, and content source.
 * It also allows for tagging the content.
 * @returns {React.ReactElement} The rendered form.
 */
const JobAidsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    duration: "",
    status: 1, // 1 = Published, 0 = Withdrawn
    updated_by: 1,
    skills: [],
    jobAids_url: "",
    content_type: 'url',
  });
const [coverPreview, setCoverPreview] = useState("");
const [filePreview, setFilePreview] = useState("");
const [errors, setErrors] = useState({});
const [tabValue, setTabValue] = useState(0);
const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
// File dropzone for file upload tab
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setFormData((prev) => ({
          ...prev,
          file: file,
          jobAids_url: file.name,
        }));
        // setPreview(URL.createObjectURL(file));
        setFilePreview(URL.createObjectURL(file));

      }
    },
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    maxFiles: 1,
    disabled: tabValue !== 1, // Only active when file tab is selected
  });

  const { getRootProps: getCoverRootProps, getInputProps: getCoverInputProps } =
    useDropzone({
      onDrop: (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file) {
          setFormData((prev) => ({
            ...prev,
            cover_picture: file,
          }));
          // setPreview(URL.createObjectURL(file));
          setCoverPreview(URL.createObjectURL(file));

        }
      },
      accept: {
        "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      },
      multiple: false,
    });
    
  useEffect(() => {
  if (id) {
    const fetchJobAid = async () => {
      try {
        const res = await jobAidService.getById(id); // You'll define this API call
        const data = res.data.data; 


      // Determine content type based on URL
      const isUrl = data.jobAids_url && (
        data.jobAids_url.startsWith('http') ||
        data.jobAids_url.startsWith('www')
      );

        setFormData((prev) => ({
          ...prev,
          name: data.name || "",
          description: data.description || "",
          jobAids_url: data.jobAids_url || "",
          duration: data.duration || "",
          status: data.status,
          created_by: data.created_by || 1,
          skills: data.skills || [],
          content_type: isUrl ? 'url' : 'file',
          // file: null, // not storing file object
        }));

         setTabValue(isUrl ? 0 : 1);

if (data.cover_picture) {
  setCoverPreview(
    data.cover_picture.startsWith("/uploads") ? data.cover_picture : `/uploads/job-aids/${data.cover_picture}`
  );
}

if (!isUrl && data.jobAids_url) {
  setFilePreview(
    data.jobAids_url.startsWith("/uploads") ? data.jobAids_url : `/uploads/job-aids/${data.jobAids_url}`
  );
}

      
      } catch (error) {
        console.error("Failed to fetch Job Aid:", error);
        setSnackbar({
          open: true,
          message: "Failed to load Job Aid",
          severity: "error",
        });
      }
    };

    fetchJobAid();
  }
}, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setFormData((prev) => ({
      ...prev,
      content_type: newValue === 0 ? "url" : "file",
      // Clear the other type's data
      ...(newValue === 0 ? { file: null } : { jobAids_url: '' }),
    }));
  };
const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Job Aid name is required";
    }

   if (!stripHtml(formData.description).trim()) {
    newErrors.description = 'Job Aid description is required';
  }
    if (formData.content_type === "url" && !formData.jobAids_url) {
      newErrors.jobAids_url = "URL is required";
    }

    if (formData.content_type === "file" && !formData.file && !coverPreview ) {
      newErrors.file = "File is required";
    }

    if (!formData.cover_picture && !coverPreview ) {
      newErrors.cover_picture = 'Course cover image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateForm();
    if (!isValid) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("name", formData.name);
      payload.append("description", formData.description);
      // payload.append("jobAids_url", formData.jobAids_url); 
      payload.append("duration", formData.duration);
      payload.append("status", formData.status);
      payload.append("updated_by", formData.updated_by);
      payload.append("skills", JSON.stringify(formData.skills)); 
      payload.append("content_type", formData.content_type);

      // if (formData.file) {
      //   payload.append("file", formData.file);
      // }

      if (formData.content_type === 'url') {
        payload.append('title', formData.name);
        payload.append('jobAids_url', formData.jobAids_url);

      } else if (formData.file) {
        payload.append('file_names', formData.name);
        if (formData.file) {
          payload.append('content_file', formData.file);
        }
      }
      if (formData.cover_picture instanceof File) {
         payload.append("cover_picture", formData.cover_picture);
}

      const response = id
      ? await jobAidService.edit(id, payload)
      : await jobAidService.create(payload);

      if (response.data.message) {
        setSnackbar({
          open: true,
          message: "Job Aid saved!",
          severity: "success",
        });
      }
       setTimeout(() => {
        navigate(`/job-aids`);
      }, 1000);
    } catch (error) {
      console.error(error);
     setSnackbar({
      open: true,
      message: error?.response?.data?.message || "Error saving Job Aid",
      severity: "error",
    });
    } finally {
      setLoading(false);
    }
  };

  const getFileType = (filename) => {
    if (!filename) return "unknown";

    const extension = filename.toLowerCase().split(".").pop();

    if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extension)
    ) {
      return "image";
    } else if (["pdf"].includes(extension)) {
      return "pdf";
    } else if (["doc", "docx", "rtf", "txt"].includes(extension)) {
      return "document";
    } else if (["ppt", "pptx"].includes(extension)) {
      return "presentation";
    } else if (["xls", "xlsx", "csv"].includes(extension)) {
      return "spreadsheet";
    } else if (["zip"].includes(extension)) {
      // Check if it might be a SCORM package based on filename
      if (filename.toLowerCase().includes("scorm")) {
        return "scorm";
      }
      return "archive";
    } else if (
      ["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(extension)
    ) {
      return "video";
    } else if (["mp3", "wav", "ogg", "m4a", "flac"].includes(extension)) {
      return "audio";
    }
    return "file";
  };
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 7 }}
      >
        <MuiAlert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Typography variant="h4" sx={{ mb: 4 }}>
        {id ? "Edit Job Aids" : "Create New Job Aids"}
      </Typography>
<FormControl fullWidth sx={{ mb: 4 }}>
      <form onSubmit={handleSubmit}>
        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Job Aids Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 3 }}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
</FormSection>
<FormSection>
          <TextField
            fullWidth
            label="Duration (minutes)"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 4, mt: 2 }}
            type="number"
            inputProps={{
              min: 1,
              step: 1,
            }}
            error={!!errors.duration}
            helperText={errors.duration || "Enter content duration in minutes"}
            required
          />
        </FormSection>

        <FormSection>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Description
          </Typography>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(content) =>
              setFormData((prev) => ({
                ...prev,
                description: content,
              }))
            }
            style={{ height: "200px", marginBottom: "50px" }}
            modules={modules}
            formats={formats}
          />
          {errors.description && (
        <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
          {errors.description}
        </Typography>
      )}
        </FormSection>

        <Typography variant="h6" sx={{ mb: 1 }}>
          Cover Image
        </Typography>

        <DropzoneArea {...getCoverRootProps()}>
          <input {...getCoverInputProps()} />
          {coverPreview  ? (
            <Box position="relative">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setCoverPreview("");
                  setFormData((prev) => ({ ...prev, cover_picture: null }));
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "white",
                  "&:hover": { backgroundColor: "white" },
                }}
              >
                <Close />
              </IconButton>
              <PreviewImage src={coverPreview } alt="Cover preview" />
            </Box>
          ) : (
            <Box>
              <Upload sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag and drop your cover image
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to browse
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                Supported formats: Images [.jpeg, .jpg, .png]
              </Typography>
            </Box>
          )}
        </DropzoneArea>

        {errors.cover_picture && (
          <Typography
            color="error"
            variant="caption"
            display="block"
            sx={{ mt: 1 }}
          >
            {errors.cover_picture}
          </Typography>
        )}

        <FormSection>
          <Typography variant="h6" sx={{ mb: 1,mt:3 }}>
            Job Aids Source
          </Typography>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="job aids type tabs"
            sx={{ mb: 3 }}
          >
            <Tab icon={<LinkIcon />} label="URL" />
            <Tab icon={<Upload />} label="File Upload" />
          </Tabs>

          {tabValue === 0 ? (
            <TextField
              fullWidth
              label="Job Aids URL"
              name="jobAids_url"
              value={formData.jobAids_url}
              onChange={handleChange}
              variant="outlined"
              placeholder="https://example.com/content"
              error={!!errors.jobAids_url}
              helperText={errors.jobAids_url}
              required
            />
          ) : (
            <DropzoneArea {...getRootProps()}>
              <input {...getInputProps()} />
              {filePreview ? (
                <Box position="relative">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilePreview("");
                      setFormData((prev) => ({
                        ...prev,
                        file: null,
                        jobAids_url: "",
                      }));
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "white" },
                    }}
                  >
                    <Close />
                  </IconButton>

                  {/* Determine what to display based on file type */}
                  {getFileType(formData.jobAids_url) === "image" ? (
                    <PreviewImage src={filePreview} alt="Job Aids preview" />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 3,
                        backgroundColor: "grey.100",
                        borderRadius: 1,
                      }}
                    >
                      {/* Show appropriate icon based on file type */}
                      {getFileType(formData.jobAids_url) === "pdf" && (
                        <PictureAsPdfIcon
                          sx={{ fontSize: 48, color: "error.main", mb: 2 }}
                        />
                      )}
                      {getFileType(formData.jobAids_url) === "document" && (
                        <DescriptionIcon
                          sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                        />
                      )}
                      {getFileType(formData.jobAids_url) === "file" && (
                        <InsertDriveFileIcon
                          sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                        />
                      )}
                      {getFileType(formData.content_url) === 'video' && (
                       <VideocamIcon 
                       sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      )}

                      {/* Display the filename */}
                      <Typography
                        variant="body1"
                        sx={{ wordBreak: "break-word", textAlign: "center" }}
                      >
                        {formData.file
                          ? formData.file.name
                          : formData.jobAids_url}
                      </Typography>

                      {/* Display file type label */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {getFileType(formData.jobAids_url) === "scorm"
                          ? "SCORM Package"
                          : getFileType(formData.jobAids_url) === "spreadsheet"
                          ? "Excel Spreadsheet"
                          : getFileType(formData.jobAids_url) === "document"
                          ? "Document"
                          : getFileType(formData.jobAids_url) === "presentation"
                          ? "Presentation"
                          : getFileType(formData.jobAids_url) === "video"
                          ? "Video"
                          : getFileType(formData.jobAids_url) === "audio"
                          ? "Audio"
                          : getFileType(formData.jobAids_url) === "pdf"
                          ? "PDF Document"
                          : getFileType(formData.jobAids_url) === "archive"
                          ? "ZIP Archive"
                          : getFileType(formData.jobAids_url) === "image"
                          ? "Image"
                          : "File"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Upload sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Drag and drop your file here
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    or click to browse
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 2, display: "block" }}
                  >
                    Supported formats: PDF, Word
                  </Typography>
                  {errors.file && (
                    <Typography
                      color="error"
                      variant="caption"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {errors.file}
                    </Typography>
                  )}
                </Box>
              )}
            </DropzoneArea>
          )}
          {tabValue === 1 && (
            <Typography variant="caption" sx={{ mt: 2 }}>
              Supported formats: PDF, Word
            </Typography>
          )}
        </FormSection>
        <FormSection>
          <SkillsInput
            value={formData.skills}
            onChange={(newSkills) =>
              setFormData((prev) => ({
                ...prev,
                skills: newSkills,
              }))
            }
            error={!!errors.skills}
            helperText={errors.skills}
            required
          />
        </FormSection>

        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            type="submit"
            variant="contained"
            // size="large"
            className="add-course-btn"
            disabled={loading}
            // sx={{ px: 3, py: 1 }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </form>
      </FormControl>
    </Box>
  );
};

export default JobAidsForm;
